/* ==========================================================================
   LUNE — Collagen Night Wrapping Mask
   Lightweight, scoped JS for the custom product template.
   - Reveal-on-scroll
   - Accessible accordion
   - Before/After comparison slider
   - Number counters
   - Quantity steppers
   - Hero gallery thumbnail switcher
   - Bundle radios + subscribe toggle (drives line item properties / selling plan)
   - Sticky mobile ATC reveal
   No external libs. No localStorage.
   ========================================================================== */
(function () {
  'use strict';

  const ready = (fn) =>
    document.readyState !== 'loading'
      ? fn()
      : document.addEventListener('DOMContentLoaded', fn);

  ready(init);

  function init() {
    setupReveal();
    setupAccordions();
    setupBeforeAfter();
    setupCounters();
    setupQty();
    setupGallery();
    setupBundleSubscribe();
    setupStickyAtc();
    setupShopifyAtcAjax();
  }

  /* ---------- Reveal on scroll ---------- */
  function setupReveal() {
    const els = document.querySelectorAll('.lune .reveal');
    if (!('IntersectionObserver' in window) || !els.length) {
      els.forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const delay = parseInt(entry.target.dataset.revealDelay || '0', 10);
            setTimeout(() => entry.target.classList.add('in'), delay);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Accordion ---------- */
  function setupAccordions() {
    document.querySelectorAll('.lune .faq-item').forEach((item) => {
      const trigger = item.querySelector('.faq-trigger');
      const panel = item.querySelector('.faq-panel');
      if (!trigger || !panel) return;
      const open = item.classList.contains('is-open');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      trigger.addEventListener('click', () => toggleFaq(item));
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleFaq(item);
        }
      });
    });
  }
  function toggleFaq(item) {
    const trigger = item.querySelector('.faq-trigger');
    const isOpen = item.classList.toggle('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  /* ---------- Before / After slider ---------- */
  function setupBeforeAfter() {
    document.querySelectorAll('.lune .ba-slider').forEach((slider) => {
      const beforeWrap = slider.querySelector('.ba-before-wrap');
      const handle = slider.querySelector('.ba-handle');
      const input = slider.querySelector('.ba-input');
      if (!beforeWrap || !handle || !input) return;
      const apply = (pos) => {
        beforeWrap.style.clipPath = `inset(0 ${100 - pos}% 0 0)`;
        handle.style.left = `${pos}%`;
      };
      apply(parseFloat(input.value) || 50);
      input.addEventListener('input', (e) => apply(parseFloat(e.target.value)));
    });
  }

  /* ---------- Counters ---------- */
  function setupCounters() {
    const els = document.querySelectorAll('.lune [data-counter-to]');
    if (!('IntersectionObserver' in window) || !els.length) {
      els.forEach((el) => {
        el.textContent = (parseFloat(el.dataset.counterTo) || 0).toLocaleString() + (el.dataset.counterSuffix || '');
      });
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounter(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    els.forEach((el) => io.observe(el));
  }
  function animateCounter(el) {
    const to = parseFloat(el.dataset.counterTo) || 0;
    const suffix = el.dataset.counterSuffix || '';
    const duration = parseInt(el.dataset.counterDuration || '1600', 10);
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(to * eased);
      el.textContent = v.toLocaleString() + suffix;
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /* ---------- Quantity stepper ---------- */
  function setupQty() {
    document.querySelectorAll('.lune .qty-stepper').forEach((stepper) => {
      const input = stepper.querySelector('input');
      const dec = stepper.querySelector('[data-qty-dec]');
      const inc = stepper.querySelector('[data-qty-inc]');
      if (!input) return;
      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '99', 10);
      const set = (v) => {
        v = Math.max(min, Math.min(max, v));
        input.value = v;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      if (dec) dec.addEventListener('click', () => set(parseInt(input.value, 10) - 1));
      if (inc) inc.addEventListener('click', () => set(parseInt(input.value, 10) + 1));
    });
  }

  /* ---------- Hero gallery thumbnails ---------- */
  function setupGallery() {
    document.querySelectorAll('.lune .hero-gallery').forEach((gallery) => {
      const main = gallery.querySelector('[data-hero-main]');
      gallery.querySelectorAll('.hero-thumb').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const src = thumb.dataset.src;
          const alt = thumb.dataset.alt || '';
          if (main && src) {
            main.innerHTML = `<img src="${src}" alt="${alt}" loading="eager" />`;
          }
          gallery.querySelectorAll('.hero-thumb').forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    });
  }

  /* ---------- Bundle radios + subscribe ---------- */
  function setupBundleSubscribe() {
    document.querySelectorAll('.lune [data-bundle-form]').forEach((form) => {
      const radios = form.querySelectorAll('input[name="bundle"]');
      const subscribeBtn = form.querySelector('[data-subscribe-toggle]');
      const subscribeInput = form.querySelector('[data-subscribe-input]');
      const priceLabel = form.querySelector('[data-final-price]');

      const recompute = () => {
        const checked = form.querySelector('input[name="bundle"]:checked');
        if (!checked) return;
        const base = parseFloat(checked.dataset.priceCents || '0') / 100;
        const sub = subscribeInput && subscribeInput.value === 'true';
        const discount = sub ? parseFloat(checked.dataset.subscribeDiscount || '0.15') : 0;
        const final = base * (1 - discount);
        if (priceLabel) {
          priceLabel.textContent = formatMoney(final, checked.dataset.currency || 'USD');
        }
        form.querySelectorAll('.bundle-option').forEach((opt) => {
          const r = opt.querySelector('input[name="bundle"]');
          opt.classList.toggle('is-selected', r && r.checked);
        });
        // Apply variant ID if the bundle option targets a specific variant
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput && checked.dataset.variantId) {
          variantInput.value = checked.dataset.variantId;
        }
        // Apply selling plan if subscribe is on
        const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
        if (sellingPlanInput) {
          if (sub && checked.dataset.sellingPlanId) {
            sellingPlanInput.value = checked.dataset.sellingPlanId;
          } else {
            sellingPlanInput.value = '';
          }
        }
        // Apply qty
        const qtyInput = form.querySelector('input[name="quantity"]');
        if (qtyInput && checked.dataset.qty) {
          qtyInput.value = checked.dataset.qty;
        }
        // Apply bundle label as a line-item property for merchants who haven't created bundle variants
        const propInput = form.querySelector('input[name="properties[Bundle]"]');
        if (propInput) propInput.value = checked.dataset.label || '';
      };

      radios.forEach((r) => r.addEventListener('change', recompute));
      if (subscribeBtn && subscribeInput) {
        subscribeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const on = subscribeInput.value !== 'true';
          subscribeInput.value = on ? 'true' : 'false';
          subscribeBtn.classList.toggle('is-on', on);
          const status = subscribeBtn.querySelector('[data-subscribe-status]');
          if (status) status.textContent = on ? 'ON' : 'OFF';
          recompute();
        });
      }
      recompute();
    });
  }

  function formatMoney(value, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(value);
    } catch (e) {
      return '$' + value.toFixed(2);
    }
  }

  /* ---------- Sticky mobile ATC reveal ---------- */
  function setupStickyAtc() {
    const bar = document.querySelector('.lune .sticky-atc');
    if (!bar) return;
    document.body.classList.add('body-pad');
    const onScroll = () => {
      bar.classList.toggle('is-show', window.scrollY > 600);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Shopify ATC ajax ---------- */
  function setupShopifyAtcAjax() {
    document.querySelectorAll('.lune form[data-product-form]').forEach((form) => {
      const submitBtn = form.querySelector('[type="submit"]');
      const errorEl = form.querySelector('.atc-error');

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const variantId = parseInt(form.querySelector('[name="id"]')?.value, 10);
        const qty = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);
        if (!variantId) return;

        submitBtn && submitBtn.setAttribute('disabled', 'disabled');
        if (errorEl) { errorEl.textContent = ''; errorEl.classList.remove('show'); }

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: qty }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || data.status) {
            throw new Error(data.description || data.message || 'Could not add to cart. Please try again.');
          }
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          window.dispatchEvent(new CustomEvent('cart:open'));
        } catch (err) {
          if (errorEl) {
            errorEl.textContent = err.message;
            errorEl.classList.add('show');
          }
        } finally {
          submitBtn && submitBtn.removeAttribute('disabled');
        }
      });
    });
  }
})();

